import { useEffect, useState } from 'react';

type AnyFlip = string | null | undefined;
type TransformationLike = {
    rotation?: number;
    flip?: AnyFlip;
    width?: number;
    height?: number;
    x?: number;
    y?: number;
    crop?: { x?: number; y?: number; width?: number; height?: number; zoom?: number };
};

type Options = {
    format?: 'png' | 'jpeg' | 'webp';
    quality?: number;
    crossOrigin?: 'anonymous' | 'use-credentials' | '';
};

const normFlip = (flip: AnyFlip) => {
    const f = String(flip ?? '').toLowerCase();
    return { fx: f === 'horizontal' || f === 'x', fy: f === 'vertical' || f === 'y' };
};

const rotatedSize = (w: number, h: number, rad: number) => {
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    return { width: Math.round(w * cos + h * sin), height: Math.round(w * sin + h * cos) };
};

export function useTransformedImage(
    originalUrl: string | undefined,
    t: TransformationLike | undefined,
    opts: Options = {},
) {
    const [url, setUrl] = useState<string | null>(null);
    const { format = 'png', quality = 0.92, crossOrigin = 'anonymous' } = opts;

    useEffect(() => {
        let revoked: string | null = null;
        let cancelled = false;

        async function run() {
            if (!originalUrl) {
                setUrl(null);
                return;
            }

            const img = new Image();
            if (crossOrigin) img.crossOrigin = crossOrigin;
            const loaded = new Promise<HTMLImageElement>((res, rej) => {
                img.onload = () => res(img);
                img.onerror = rej;
            });
            img.src = originalUrl;
            const el = await loaded;

            const srcW = el.naturalWidth || el.width;
            const srcH = el.naturalHeight || el.height;

            const deg = ((Math.round(t?.rotation ?? 0) % 360) + 360) % 360;
            const rad = (deg * Math.PI) / 180;
            const { fx, fy } = normFlip(t?.flip);

            let stageW = Math.round(t?.width ?? 0);
            let stageH = Math.round(t?.height ?? 0);
            const looksLikeCropDims =
                !!t?.crop?.width &&
                !!t?.crop?.height &&
                t?.width === t?.crop?.width &&
                t?.height === t?.crop?.height;
            if (!stageW || !stageH || looksLikeCropDims) {
                const rs = rotatedSize(srcW, srcH, rad);
                stageW = rs.width;
                stageH = rs.height;
            }

            const stage = document.createElement('canvas');
            stage.width = stageW;
            stage.height = stageH;
            const sctx = stage.getContext('2d');
            if (!sctx) {
                setUrl(null);
                return;
            }

            sctx.imageSmoothingEnabled = true;
            sctx.imageSmoothingQuality = 'high';
            sctx.save();
            sctx.translate(stageW / 2, stageH / 2);
            if (fx || fy) sctx.scale(fx ? -1 : 1, fy ? -1 : 1);
            if (rad) sctx.rotate(rad);
            sctx.drawImage(el, -srcW / 2, -srcH / 2, srcW, srcH);
            sctx.restore();

            const cx = Math.round(t?.crop?.x ?? t?.x ?? 0);
            const cy = Math.round(t?.crop?.y ?? t?.y ?? 0);
            const cw = Math.round(t?.crop?.width ?? 0);
            const ch = Math.round(t?.crop?.height ?? 0);
            const zoom = t?.crop?.zoom ?? 1;

            const outW = cw > 0 ? Math.round(cw * zoom) : stageW;
            const outH = ch > 0 ? Math.round(ch * zoom) : stageH;

            const out = document.createElement('canvas');
            out.width = outW;
            out.height = outH;
            const octx = out.getContext('2d');
            if (!octx) {
                setUrl(null);
                return;
            }

            octx.imageSmoothingEnabled = true;
            octx.imageSmoothingQuality = 'high';

            if (cw > 0 && ch > 0) {
                octx.drawImage(stage, cx, cy, cw, ch, 0, 0, outW, outH);
            } else {
                octx.drawImage(stage, 0, 0, stageW, stageH, 0, 0, outW, outH);
            }

            const blob: Blob = await new Promise((resolve, reject) => {
                const done = (b: Blob | null) =>
                    b ? resolve(b) : reject(new Error('toBlob failed'));
                if (format === 'png') return out.toBlob(done, 'image/png');
                if (format === 'jpeg') return out.toBlob(done, 'image/jpeg', quality);
                return out.toBlob(done, 'image/webp', quality);
            });

            const objUrl = URL.createObjectURL(blob);
            revoked = objUrl;
            if (!cancelled) setUrl(objUrl);
        }

        run().catch(() => setUrl(null));
        return () => {
            cancelled = true;
            if (revoked) URL.revokeObjectURL(revoked);
        };
    }, [
        originalUrl,
        t?.rotation,
        t?.flip,
        t?.width,
        t?.height,
        t?.crop?.x,
        t?.crop?.y,
        t?.crop?.width,
        t?.crop?.height,
        t?.crop?.zoom,
        t?.x,
        t?.y,
        format,
        quality,
        crossOrigin,
    ]);

    return url;
}
